function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useCallback, useMemo } from 'react';
import { BaseEdge, EdgeLabelRenderer, type EdgeProps, getBezierPath } from '@xyflow/react';
import isNull from 'lodash/isNull';
import { type QueryKey, useIsFetching, useQueryClient } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';
import { useParams } from 'react-router';
import { getCorrelationScore } from 'common/report/canvas/utils';
import type { MetricsResponse } from 'common/react-query/metrics/types';
import { metricsNodeQueryKeyPrefix } from 'common/report/canvas/react-query';
import { useTranslation } from 'common/i18n/hooks';
import { DateRange } from 'common/widgets/date-range-picker/models/date-range';
import { getActiveBoardFilters } from 'apps/boards/src/dashboards-app/util';
import { insightsFilterAttrsFromPropertyFilter } from 'common/widgets/property-filter-menu/models/insights-filter';
import { DATASET_MIXPANEL } from 'common/report/constants';
import { Visibility } from 'iron/common/report/canvas/types';
import { AppState, useCanvasStore } from 'iron/common/report/canvas/store';
import { buildMetricRequest } from 'iron/common/report/canvas/nodes/metric-node/utils';
import { MetricNodeType } from 'common/report/canvas/nodes/metric-node/types';
import { isMetricNode } from 'common/report/canvas/nodes/types';
import { CorrelationEdgeType } from './types';
export enum EdgeColorType {
  UNKNOWN = "UNKNOWN",
  SELECTED = "SELECTED",
  POSITIVE_STRONG = "POSITIVE_STRONG",
  POSITIVE_MODERATE = "POSITIVE_MODERATE",
  POSITIVE_WEAK = "POSITIVE_WEAK",
  NEGATIVE_WEAK = "NEGATIVE_WEAK",
  NEGATIVE_MODERATE = "NEGATIVE_MODERATE",
  NEGATIVE_STRONG = "NEGATIVE_STRONG",
}
export const STROKE_WIDTH = 2;
export const EDGE_COLORS = {
  [EdgeColorType.UNKNOWN]: "#8F8F91",
  [EdgeColorType.SELECTED]: "#4F44E0",
  [EdgeColorType.POSITIVE_STRONG]: "#0793D1",
  [EdgeColorType.POSITIVE_MODERATE]: "#0793D1",
  [EdgeColorType.POSITIVE_WEAK]: "#8F8F91",
  [EdgeColorType.NEGATIVE_WEAK]: "#8F8F91",
  [EdgeColorType.NEGATIVE_MODERATE]: "#DD731C",
  [EdgeColorType.NEGATIVE_STRONG]: "#DD731C"
};
export const PILL_COLORS = {
  [EdgeColorType.UNKNOWN]: "#F4F6FA",
  [EdgeColorType.SELECTED]: "#4F44E0",
  [EdgeColorType.POSITIVE_STRONG]: "#8AD8ED",
  [EdgeColorType.POSITIVE_MODERATE]: "#C6E9F6",
  [EdgeColorType.POSITIVE_WEAK]: "#F4F6FA",
  [EdgeColorType.NEGATIVE_WEAK]: "#F4F6FA",
  [EdgeColorType.NEGATIVE_MODERATE]: "#FDF5CF",
  [EdgeColorType.NEGATIVE_STRONG]: "#F6E07D"
};
const selector = (state: AppState) => ({
  timeRangesAndComparisons: state.timeRangesAndComparisons,
  activeTimeRangeIndex: state.activeTimeRangeIndex,
  asOfDate: state.asOfDate,
  getNode: state.getNode,
  globalFilters: state.globalFilters,
  visibility: state.visibility
});
export function CorrelationEdge(_ref: EdgeProps<CorrelationEdgeType>) {
  let {
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    source,
    target,
    selected,
    style = {}
  }: EdgeProps<CorrelationEdgeType> = _ref;
  const {
    projectId,
    workspaceId
  } = useParams<{
    projectId: string;
    workspaceId?: string;
  }>();
  const {
    timeRangesAndComparisons: globalTimeRangesAndComparisons,
    activeTimeRangeIndex,
    asOfDate,
    getNode,
    globalFilters,
    visibility
  } = useCanvasStore(useShallow(selector));
  const additionalFilters = getActiveBoardFilters(globalFilters).map(propertyFilter => {
    return insightsFilterAttrsFromPropertyFilter(_objectSpread({
      dataset: DATASET_MIXPANEL
    }, propertyFilter));
  });
  const sourceNode = getNode<MetricNodeType>(source)!;
  const targetNode = getNode<MetricNodeType>(target)!;
  let sourceRequest = null;
  if (sourceNode && isMetricNode(sourceNode)) {
    var _activeTimeRangeIndex;
    const timeRangesAndComparisons = sourceNode.data.timeRangesAndComparisons || globalTimeRangesAndComparisons;
    const index = (_activeTimeRangeIndex = activeTimeRangeIndex[sourceNode.id]) !== null && _activeTimeRangeIndex !== void 0 ? _activeTimeRangeIndex : 0;
    sourceRequest = buildMetricRequest({
      nodeData: sourceNode.data,
      asOfDate,
      dateRange: new DateRange(timeRangesAndComparisons[index].timeRange),
      timeComparison: timeRangesAndComparisons[index].timeComparison,
      additionalFilters,
      projectId: projectId!,
      workspaceId
    });
  }
  let targetRequest = null;
  if (targetNode && isMetricNode(targetNode)) {
    var _activeTimeRangeIndex2;
    const timeRangesAndComparisons = targetNode.data.timeRangesAndComparisons || globalTimeRangesAndComparisons;
    const index = (_activeTimeRangeIndex2 = activeTimeRangeIndex[targetNode.id]) !== null && _activeTimeRangeIndex2 !== void 0 ? _activeTimeRangeIndex2 : 0;
    targetRequest = buildMetricRequest({
      nodeData: targetNode.data,
      asOfDate,
      dateRange: new DateRange(timeRangesAndComparisons[index].timeRange),
      timeComparison: timeRangesAndComparisons[index].timeComparison,
      additionalFilters,
      projectId: projectId!,
      workspaceId
    });
  }
  const sourcePrefix = sourceRequest ? metricsNodeQueryKeyPrefix(sourceRequest) : [];
  const targetPrefix = targetRequest ? metricsNodeQueryKeyPrefix(targetRequest) : [];
  const isFetchingSource = useIsFetching({
    queryKey: sourcePrefix
  });
  const isFetchingTarget = useIsFetching({
    queryKey: targetPrefix
  });
  const queryClient = useQueryClient();
  const i18n = useTranslation();
  const getQueriesData = useCallback((prefix: QueryKey) => {
    var _ref2, _queryClient$getQueri;
    return (_ref2 = ((_queryClient$getQueri = queryClient.getQueriesData({
      queryKey: prefix
    })) === null || _queryClient$getQueri === void 0 || (_queryClient$getQueri = _queryClient$getQueri[0]) === null || _queryClient$getQueri === void 0 ? void 0 : _queryClient$getQueri[1]) as MetricsResponse) !== null && _ref2 !== void 0 ? _ref2 : null;
  }, [queryClient]);
  const sourceData = useMemo(() => getQueriesData(sourcePrefix), [getQueriesData, sourcePrefix, isFetchingSource]);
  const targetData = useMemo(() => getQueriesData(targetPrefix), [getQueriesData, targetPrefix, isFetchingTarget]);
  const correlationScore = useMemo(() => getCorrelationScore(sourceData, targetData), [sourceData, targetData]);
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition
  });
  const {
    edgeColor,
    pillColor,
    type
  } = useMemo(() => {
    let colorType = EdgeColorType.UNKNOWN;
    if (isNull(correlationScore)) {
      return {
        edgeColor: EDGE_COLORS[colorType],
        pillColor: PILL_COLORS[colorType],
        type: EdgeColorType[colorType]
      };
    }
    if (correlationScore > 0.6) {
      colorType = EdgeColorType.POSITIVE_STRONG;
    } else if (correlationScore > 0.3) {
      colorType = EdgeColorType.POSITIVE_MODERATE;
    } else if (correlationScore >= 0) {
      colorType = EdgeColorType.POSITIVE_WEAK;
    } else if (correlationScore < -0.6) {
      colorType = EdgeColorType.NEGATIVE_STRONG;
    } else if (correlationScore < -0.3) {
      colorType = EdgeColorType.NEGATIVE_MODERATE;
    } else if (correlationScore < 0) {
      colorType = EdgeColorType.NEGATIVE_WEAK;
    }
    return {
      edgeColor: EDGE_COLORS[colorType],
      pillColor: PILL_COLORS[colorType],
      type: colorType
    };
  }, [correlationScore]);
  const showCorrelation = visibility[Visibility.CORRELATION];
  const markerEnd = useMemo(() => "url(#".concat(selected ? EdgeColorType.SELECTED : showCorrelation ? type : EdgeColorType.UNKNOWN, ")"), [type, selected, showCorrelation]);
  const edgeStyle = useMemo(() => _objectSpread(_objectSpread({}, style), {}, {
    strokeWidth: STROKE_WIDTH,
    stroke: selected ? EDGE_COLORS[EdgeColorType.SELECTED] : showCorrelation ? edgeColor : EDGE_COLORS[EdgeColorType.UNKNOWN],
    strokeDasharray: "4 4"
  }), [edgeColor, selected, showCorrelation]);
  const label = useMemo(() => {
    if (isNull(correlationScore)) {
      return i18n.t("canvases.edges.correlation.unknown");
    }
    const score = correlationScore.toFixed(2);
    switch (type) {
      case EdgeColorType.POSITIVE_STRONG:
        return i18n.t("canvases.edges.correlation.strong-positive", {
          score
        });
      case EdgeColorType.POSITIVE_MODERATE:
        return i18n.t("canvases.edges.correlation.moderate-positive", {
          score
        });
      case EdgeColorType.POSITIVE_WEAK:
        return i18n.t("canvases.edges.correlation.weak-positive", {
          score
        });
      case EdgeColorType.NEGATIVE_WEAK:
        return i18n.t("canvases.edges.correlation.weak-negative", {
          score
        });
      case EdgeColorType.NEGATIVE_MODERATE:
        return i18n.t("canvases.edges.correlation.moderate-negative", {
          score
        });
      case EdgeColorType.NEGATIVE_STRONG:
        return i18n.t("canvases.edges.correlation.strong-negative", {
          score
        });
      default:
        return i18n.t("canvases.edges.correlation.unknown");
    }
  }, [type, correlationScore]);
  return <>
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={edgeStyle} data-sentry-element="BaseEdge" data-sentry-source-file="index.tsx" />
      {showCorrelation && correlationScore && <EdgeLabelRenderer>
          <div className='correlation-edge' style={{
        position: "absolute",
        transform: "translate(-50%, -50%) translate(".concat(labelX, "px,").concat(labelY, "px)"),
        background: pillColor,
        color: "#2A2A2F",
        paddingTop: 4,
        paddingBottom: 4,
        paddingLeft: 6,
        paddingRight: 8,
        borderRadius: 40,
        fontSize: 14,
        fontWeight: 500,
        pointerEvents: "visible"
      }}>
            {label}
          </div>
        </EdgeLabelRenderer>}
    </>;
}